import { selectBTaxAccount, selectBTaxAccountBusinessAddress, selectBTaxAccountBusinessName, selectBTaxAccountExemptionStatus, selectBTaxAccountFictitiousName, selectBTaxAccountFriendlyTitle, selectBTaxAccountMailingAddress, selectBTaxAccountOwnerName, selectBTaxAccountStartDate, selectBTaxAccountStatus, selectBTaxAccountTypesOfBusiness } from '@/app/modules/btax/state/entities/account/btax-account.selectors';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { selectBTaxAccountDetailViewAvailability, selectBTaxDisclaimer } from '@btax/state/btax-state/btax-state.selectors';
import { billsForBTaxAccountLoaded, billsForBTaxAccountLoadFailed, loadBillsForBTaxAccount } from '@btax/state/btax.actions';
import { ofType } from '@ces/sourced-action';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { JumbotronItemStatus } from '@shared/components/data-jumbotron/data-jumbotron.component';
import { State } from '@state/model';
import { BTaxAccountStatus, BTAX_ACCOUNT_STATUS_ICONS, BTAX_ACCOUNT_STATUS_NAMES, EgovAPIErrorType, isEgovAPIError } from 'egov-api';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { loadBTaxState } from '../../state/btax-state/btax-state.actions';
import { selectBTaxSupportedByState } from '../../state/btax.selectors';

@Component({
    selector: 'btax-account',
    templateUrl: './btax-account.component.html',
    styleUrls: ['./btax-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BTaxAccountPage implements OnInit, OnDestroy // eslint-disable-line @angular-eslint/component-class-suffix
{
    constructor
    (
        // Dependencies

        private readonly route: ActivatedRoute,
        private readonly store: Store<State>,
        private readonly actions$: Actions,
    )
    {}


    // Initialization

    public ngOnInit()
    {
        this.store.dispatch(loadBTaxState('BTaxAccountPage'));
        this.initializeNavigationHandling();
    }


    // URL values

    protected readonly accountNumber$ = new BehaviorSubject<string | undefined>(undefined);


    // Navigation

    private urlParamsSubscription?: Subscription;

    protected initializeNavigationHandling()
    {
        this.urlParamsSubscription = this.route.params.subscribe(params =>
        {
            if (params.accountNumber)
            {
                this.accountNumber$.next(params.accountNumber);
                this.loadBills();
            }
            else if (!this.accountNumber$.value)
                // If a value is already set, lack of value probably means navigating out
                throw new Error('Account number not passed.');
        });
    }


    // Load bills

    protected readonly accountNotFound$ = new BehaviorSubject(false);

    protected loadError = false;
    protected loadErrorRetry = false;
    protected loading = true;

    private billsLoadedSubscription?: Subscription;
    private billsLoadFailedSubscription?: Subscription;

    private loadBillsCounter = Number.MIN_SAFE_INTEGER;

    private loadBills()
    {
        this.accountNotFound$.next(false);
        this.loadError = false;
        this.loading = true;

        // Dispatch action

        const operationId = `BTaxAccountPage .loadBills() ${this.loadBillsCounter++}`;

        this.store.dispatch(loadBillsForBTaxAccount('BTaxAccountPage .loadBills()', {
            operationId,
            accountNumber: this.accountNumber$.value!
        }));


        // Bills loaded or load error

        const billsLoaded$ = this.actions$.pipe(
            ofType(billsForBTaxAccountLoaded),
            filter(action => action.operationId === operationId)
        );

        this.billsLoadedSubscription = billsLoaded$.subscribe(action =>
        {
            this.loading = false;
        });


        // Bills load error

        const billsLoadFailed$ = this.actions$.pipe(
            ofType(billsForBTaxAccountLoadFailed),
            filter(action => action.operationId === operationId)
        );

        this.billsLoadFailedSubscription = billsLoadFailed$.subscribe(({ error }) =>
        {
            this.loading = false;

            if (isEgovAPIError(error))
            {
                if (error.type === EgovAPIErrorType.NOT_FOUND)
                    this.accountNotFound$.next(true);
                else
                {
                    this.loadError = true;
                    this.loadErrorRetry = !error.permanent;
                }
            }
            else
            {
                this.loadError = true;
                this.loadErrorRetry = true;
            }
        });
    }

    protected retry() { this.loadBills(); }


    // Service state / settings

    protected readonly btaxSupported$ = this.store.select(selectBTaxSupportedByState);

    protected readonly accountDetailViewIsUnavailable$ = this.store.select(selectBTaxAccountDetailViewAvailability).pipe(
        map(a => a === undefined ? a : !a),
    );

    protected readonly disclaimers$ = combineLatest([this.store.select(selectBTaxDisclaimer)]);


    // BTax Entities

    protected readonly account$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccount(accountNumber))
            : of(undefined)
        )
    );

    protected readonly title$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccountFriendlyTitle(accountNumber))
            : of(undefined)
        ),
    );


    // Jumbotron

    private static readonly ACCOUNT_STATUS_STYLES = {
        [BTaxAccountStatus.ACTIVE]: JumbotronItemStatus.GOOD,
        [BTaxAccountStatus.PENDING]: JumbotronItemStatus.WARNING,
        [BTaxAccountStatus.CLOSED]: JumbotronItemStatus.BAD,
        [BTaxAccountStatus.CATEGORY_DELETED]: JumbotronItemStatus.NORMAL,
        [BTaxAccountStatus.TBD]: JumbotronItemStatus.WARNING,
    };

    protected readonly jumbotronItems$ = this.account$.pipe(
        switchMap(account => account
            ? combineLatest([
                this.store.select(selectBTaxAccountBusinessName(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: 'store',
                        label: 'Business Name',
                        value
                    })
                ),
                this.store.select(selectBTaxAccountFictitiousName(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: 'star',
                        label: 'Fictitious Name',
                        value
                    })
                ),
                this.store.select(selectBTaxAccountOwnerName(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: 'person',
                        label: 'Owner',
                        value
                    })
                ),
                this.store.select(selectBTaxAccountStartDate(account.accountNumber)).pipe(
                    map(value => value !== undefined && !isNaN(value) && {
                        icon: 'event_available',
                        label: 'Business Start Date',
                        value: new DatePipe('en_US').transform(value, 'longDate'),
                    })
                ),
                this.store.select(selectBTaxAccountStatus(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: BTAX_ACCOUNT_STATUS_ICONS[value],
                        label: 'Status',
                        value: BTAX_ACCOUNT_STATUS_NAMES[value],
                        status: BTaxAccountPage.ACCOUNT_STATUS_STYLES[value],
                    })
                ),
                this.store.select(selectBTaxAccountBusinessAddress(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: 'business',
                        label: 'Business Address',
                        value,
                    })
                ),
                this.store.select(selectBTaxAccountMailingAddress(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: 'mail',
                        label: 'Mailing Address',
                        value,
                    })
                ),
                this.store.select(selectBTaxAccountExemptionStatus(account.accountNumber)).pipe(
                    map(value => value !== undefined && {
                        icon: 'alt_route',
                        label: 'Exemption Status',
                        value,
                    })
                ),
                this.store.select(selectBTaxAccountTypesOfBusiness(account.accountNumber)).pipe(
                    map(value => value && {
                        icon: 'business_center',
                        label: 'Business / Occupation',
                        value: value.length ? value.map(v => v.description) : 'None',
                    })
                ),
            ]).pipe(map(items => items.filter(v => v)))
            : of(undefined)
        ),
    );


    // Clear subscriptions

    public ngOnDestroy()
    {
        if (this.billsLoadedSubscription) this.billsLoadedSubscription.unsubscribe();
        if (this.billsLoadFailedSubscription) this.billsLoadFailedSubscription.unsubscribe();
        this.urlParamsSubscription!.unsubscribe();
    }

}
